import { Link, useSearchParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { StatCard } from "@/components/data-display/stat-card";
import { InfoBanner } from "@/components/ui/banner";
import { EmptyState } from "@/components/ui/feedback";
import { SupportDetails } from "@/components/ui/copyable-reference";
import { IdentifierValue } from "@/components/ui/identifier-value";
import { LoadingState } from "@/components/ui/system-state";
import { SigningTopologyChip } from "@/components/data-display/signing-topology-chip";
import { useErrorLocale } from "@/i18n/use-error-locale";
import WalletActivityPanel from "@/features/wallets/wallet-activity-panel";
import { cn } from "@/lib/utils";
import type { WalletActivity, WalletDetail, WalletRecentTransfer } from "@/types";

// Property keys whose value is an opaque identifier (a wallet id, an
// on-chain address, or a profile id) rather than a short human label. These
// get the middle-ellipsis + copy treatment (ALL-4921); everything else is
// short enough to render as plain, defensively-truncated text.
const IDENTIFIER_PROPERTY_KEYS = new Set(["Wallet ID", "Address", "Wallet Profile"]);

const ASSET_TONE: Record<string, string> = {
  ETH: "var(--ads-asset-eth)",
  USDC: "var(--ads-asset-usdc)",
  USDT: "var(--ads-asset-usdt)",
};

const CROSS_MODULE_HINT = "Available once the linked module is built";

function recentTransfersErrorMessage(reasonCode: string): string {
  if (reasonCode.startsWith("AUTH_") || reasonCode === "HTTP_401" || reasonCode === "HTTP_403") {
    return "You do not have permission to view recent transfers for this wallet.";
  }
  if (reasonCode === "NETWORK_ERROR" || /^HTTP_5\d\d$/.test(reasonCode)
      || reasonCode.endsWith("_TIMEOUT") || reasonCode.endsWith("_UNAVAILABLE")) {
    return "Recent transfers could not be loaded. Try Refresh.";
  }
  return "Recent transfers are unavailable. Contact support if this persists.";
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-4 overflow-hidden rounded-lg border border-line bg-surface">
      <h2 className="border-b border-line px-5 pb-3.5 pt-[18px] text-[15px] font-bold text-ink">{title}</h2>
      {children}
    </section>
  );
}

function DisabledLink({ children, title }: { children: React.ReactNode; title: string }) {
  return (
    <span aria-disabled title={title} className="font-semibold text-primary">
      {children}
    </span>
  );
}

export default function WalletDetailView({
  detail,
  activity = null,
  activityLoading = false,
  recentTransfers,
  recentTransfersLoading = false,
  recentTransfersErrorCode = null,
}: {
  detail: WalletDetail;
  /** ALL-5458: incoming funds for this wallet; null renders the loading state until the first answer. */
  activity?: WalletActivity | null;
  activityLoading?: boolean;
  recentTransfers?: WalletRecentTransfer[];
  recentTransfersLoading?: boolean;
  recentTransfersErrorCode?: string | null;
}) {
  const [searchParams] = useSearchParams();
  const errorCatalog = useErrorLocale(searchParams);
  // A row is only a current balance when the read that produced it succeeded.
  // `unavailable` / `unsupported` mean the numbers are unknown even if the
  // response carried a stale list, so the banners below are the whole panel
  // (ALL-5553): unknown must not be presented as a balance the user can act on.
  const balancesUnknown = detail.balanceStatus === "unavailable" || detail.balanceStatus === "unsupported";
  const shownTransfers = recentTransfers ?? detail.recentTransfers;
  return (
    <>
      <Breadcrumb items={[{ label: "Wallets", href: "/wallets" }, { label: detail.name }]} />

      <div className="mb-4 flex items-center gap-2">
        <h1 className="text-lg font-bold text-ink">{detail.name}</h1>
        <SigningTopologyChip signingTopology={detail.signingTopology} />
      </div>

      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {detail.stats.map((s) => (
          <StatCard key={s.label} value={s.value} label={s.label} hint={s.hint} positiveHint={s.positiveHint} tone={s.tone} />
        ))}
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_300px]">
        <div className="min-w-0">
          <Panel title="Balances">
            {detail.balanceStatus === "unavailable" && (
              <div role="alert" data-testid="wallet-balances-unavailable" className="px-5 pt-4">
                <InfoBanner variant="danger" className="mb-0" title={errorCatalog.categories.service_unavailable.title}>
                  {errorCatalog.categories.service_unavailable.message}
                  <SupportDetails reference={detail.balanceSupportReference} />
                </InfoBanner>
              </div>
            )}
            {detail.balanceStatus === "unsupported" && (
              <div data-testid="wallet-balances-unsupported" className="px-5 pt-4">
                <InfoBanner variant="warning" className="mb-0">
                  On-chain balances are not available for this wallet&apos;s network yet.
                </InfoBanner>
              </div>
            )}
            {/* A successful read that returns no assets is a result, not a
                missing one (ALL-4839 r3). Without this, an OK read with an
                empty balances array rendered a bare "Balances" heading over
                an empty <table> — indistinguishable from a read that failed,
                and the sibling surface (transfer step 3, "Available Assets")
                already states it explicitly. Only the OK status may claim
                "no assets"; a failed read keeps its own banner above. */}
            {detail.balanceStatus === "ok" && detail.balances.length === 0 && (
              <div data-testid="wallet-balances-empty" className="px-5">
                <EmptyState
                  title="No assets in this wallet"
                  description="The on-chain read succeeded and this wallet holds no assets. Balances appear here once funds are received."
                />
              </div>
            )}
            {!balancesUnknown && detail.balances.length > 0 && (
              <table className="w-full border-collapse">
                <tbody>
                  {detail.balances.map((b, i) => (
                    <tr key={b.symbol} className={cn(i < detail.balances.length - 1 && "border-b border-line")}>
                      <td className="px-5 py-3.5 align-middle">
                        <div className="flex items-center gap-2.5">
                          <span
                            aria-hidden
                            className="grid size-7 shrink-0 place-items-center rounded-full text-[10px] font-bold text-white"
                            style={{ background: ASSET_TONE[b.symbol] ?? "var(--ads-muted)" }}
                          >
                            {b.iconLabel}
                          </span>
                          <div>
                            <div className="text-sm font-medium text-ink">{b.name}</div>
                            <div className="text-[13px] text-muted">{b.amount}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-right text-sm font-semibold text-ink">{b.usd}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>

          <WalletActivityPanel activity={activity} loading={activityLoading} />

          <Panel title="Route Capabilities">
            <div className="grid grid-cols-1 sm:grid-cols-2">
              {detail.routes.map((r, i) => (
                <div
                  key={r.title}
                  className={cn(
                    "border-line px-5 py-4",
                    i % 2 === 0 && "sm:border-r",
                    i < detail.routes.length - (detail.routes.length % 2 === 0 ? 2 : 1) && "border-b"
                  )}
                >
                  <div className="text-sm font-semibold text-ink">{r.title}</div>
                  <div className={cn("text-[13px] font-semibold", r.supported ? "text-success" : "text-danger")}>
                    {r.supported ? "✓ Supported" : "✗ Not Available"}
                  </div>
                  <div className="mt-1 text-xs text-muted">{r.detail}</div>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Recent Transfers">
            {recentTransfersLoading ? (
              // F29: a bare "Loading transfers…" paragraph is indistinguishable
              // from a finished-but-empty panel. Skeleton bars in a status
              // region show the shape of what is arriving and announce
              // themselves to a screen reader.
              <div className="px-5 py-4">
                <LoadingState rows={2} />
              </div>
            ) : recentTransfersErrorCode ? (
              <p role="alert" className="px-5 py-4 text-sm text-danger">{recentTransfersErrorMessage(recentTransfersErrorCode)}</p>
            ) : shownTransfers.length === 0 ? (
              <EmptyState title="No transfers yet." className="px-5 py-8" />
            ) : <>
              <div className="space-y-2 p-4 sm:hidden">
                {shownTransfers.map((t) => (
                  <article key={t.id} aria-label={`Transfer ${t.id}`} className="rounded-lg border border-line p-3">
                    <div className="flex min-w-0 items-start justify-between gap-2">
                      <Link to={`/transfers/${t.id}`} className="min-w-0 break-all text-[13px] font-semibold text-primary hover:underline">
                        {t.id}
                      </Link>
                      <Badge variant={t.statusVariant} dot={false} size="sm">{t.status}</Badge>
                    </div>
                    <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-[13px]">
                      <div>
                        <dt className="text-xs text-muted">Direction</dt>
                        <dd className={t.direction === "sent" ? "text-danger" : "text-success"}>
                          {t.direction === "sent" ? "↑ Sent" : "↓ Received"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-muted">Amount</dt>
                        <dd className="break-words text-ink">{t.amount} {t.asset}</dd>
                      </div>
                      <div className="col-span-2">
                        <dt className="text-xs text-muted">Time</dt>
                        <dd className="text-muted">{t.time}</dd>
                      </div>
                    </dl>
                  </article>
                ))}
              </div>
              <div className="hidden overflow-x-auto sm:block">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-line bg-bg text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-muted">
                    <th className="px-5 py-2.5">ID</th>
                    <th className="px-5 py-2.5">Direction</th>
                    <th className="px-5 py-2.5">Asset</th>
                    <th className="px-5 py-2.5">Amount</th>
                    <th className="px-5 py-2.5">Status</th>
                    <th className="px-5 py-2.5">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {shownTransfers.map((t, i) => (
                    <tr key={t.id} className={cn(i < shownTransfers.length - 1 && "border-b border-line")}>
                      <td className="px-5 py-3 text-[13px]">
                        <Link to={`/transfers/${t.id}`} className="font-semibold text-primary hover:underline">
                          {t.id}
                        </Link>
                      </td>
                      <td className={cn("px-5 py-3 text-[13px]", t.direction === "sent" ? "text-danger" : "text-success")}>
                        {t.direction === "sent" ? "↑ Sent" : "↓ Received"}
                      </td>
                      <td className="px-5 py-3 text-[13px] text-ink">{t.asset}</td>
                      <td className="px-5 py-3 text-[13px] text-ink">{t.amount}</td>
                      <td className="px-5 py-3">
                        <Badge variant={t.statusVariant} dot={false} size="sm">
                          {t.status}
                        </Badge>
                      </td>
                      <td className="px-5 py-3 text-[13px] text-muted">{t.time}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </>}
          </Panel>
        </div>

        <div className="min-w-0">
          <Panel title="Wallet Properties">
            {/* table-fixed + a fixed key/value split (ALL-4921): the browser's
                default "auto" table layout sizes every column to its widest
                cell, so one long value (a wallet id or on-chain address) grows
                the whole value column past the panel's edge. Since the panel
                clips overflow, that pushes every other row's value out of
                view too, not just the long one. table-fixed pins both column
                widths regardless of content length, so the value column has
                a real, constrained width for min-w-0/truncate to work
                against on every row and every viewport. */}
            <table className="w-full table-fixed border-collapse text-[13px]">
              <tbody>
                {detail.properties.map((p, i) => (
                  <tr key={p.k} className={cn(i < detail.properties.length - 1 && "border-b border-line")}>
                    <td className="w-[42%] px-5 py-2.5 align-top text-muted">{p.k}</td>
                    <td className="w-[58%] min-w-0 px-5 py-2.5 text-right font-medium text-ink">
                      {p.k === "Policy" ? (
                        <Link to={`/governance/${p.v}`} className="font-semibold text-primary hover:underline">
                          {p.v}
                        </Link>
                      ) : IDENTIFIER_PROPERTY_KEYS.has(p.k) ? (
                        <IdentifierValue value={p.v} className="justify-end" />
                      ) : (
                        <span className="block truncate" title={p.v}>
                          {p.v}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>

          <Panel title="Evidence">
            <div className="px-5 py-3.5">
              {detail.evidence.map((e) => (
                <div key={e.id} className="mb-2 text-[13px] last:mb-0">
                  <Link to={`/evidence/${e.id}`} className="font-semibold text-primary hover:underline">
                    {e.label}
                  </Link>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Key Recovery">
            <div className="px-5 py-3.5 text-[13px] font-medium">
              <DisabledLink title={CROSS_MODULE_HINT}>View recovery posture →</DisabledLink>
            </div>
          </Panel>

          <div className="mt-4">
            <Link
              to="/wallets"
              className="text-[13px] font-semibold text-primary transition-colors hover:underline"
            >
              ← Back to Wallets
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
